/*
  La Sura - search ideas (snippets/lasura-search-inspiration.liquid, snippets/lasura-search-ideas.liquid).

  <lasura-search-inspiration> types each idea into the empty search field, holds it, deletes it,
  and moves on. It runs one pass per opening of search, then hides and the field shows its own
  placeholder again. It stops the moment anything is typed. With reduced motion it shows one idea,
  still, until the shopper types. It is decorative (aria-hidden); the ideas are also buttons.

  Also the message examples in the quick add sheet (blocks/lasura-card-quick.liquid), with two
  options: data-field (a selector for the field in the same wrapper; default the search input)
  and data-stop-on-focus (tapping into the field stops the ideas, rather than starting them).
  That sheet starts them with play() when it opens.

  <lasura-search-ideas> makes those buttons fill the field and run Horizon's predictive search in
  place. Without this script they are ordinary links to the search page.
*/
const TYPE_MS = 55;
const DELETE_MS = 28;
const HOLD_MS = 1500;
const GAP_MS = 350;
const START_MS = 500;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class LasuraSearchInspiration extends HTMLElement {
  #ideas = [];
  #input = null;
  #typed = null;
  #timer = 0;
  #ran = false;
  #stopOnFocus = false;

  connectedCallback() {
    this.#input = this.parentElement?.querySelector(this.dataset.field || 'input.search-input') ?? null;
    this.#typed = this.querySelector('[data-typed], .lasura-search-inspiration__typed');
    this.#stopOnFocus = this.hasAttribute('data-stop-on-focus');
    try {
      const data = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent ?? '[]');
      this.#ideas = Array.isArray(data) ? data.filter((idea) => typeof idea === 'string' && idea).slice(0, 5) : [];
    } catch {
      this.#ideas = [];
    }
    if (!this.#input || !this.#typed || this.#ideas.length === 0) return;

    this.#input.addEventListener('focus', this.#stopOnFocus ? this.#stop : this.#onFocus);
    this.#input.addEventListener('input', this.#stop);
    this.closest('dialog')?.addEventListener('close', this.#onClose);
    if (!this.#stopOnFocus && document.activeElement === this.#input) this.#onFocus();
  }

  /* Start a pass now (the quick add sheet, on opening). Once per opening, and not over typing. */
  play() {
    if (this.#input && document.activeElement !== this.#input) this.#onFocus();
  }

  disconnectedCallback() {
    this.#input?.removeEventListener('focus', this.#onFocus);
    this.#input?.removeEventListener('focus', this.#stop);
    this.#input?.removeEventListener('input', this.#stop);
    this.closest('dialog')?.removeEventListener('close', this.#onClose);
    this.#stop();
  }

  #onFocus = () => {
    if (this.#ran || this.#input.value) return;
    this.#ran = true;
    this.#start();
  };

  /* Search closed: the next opening gets its own pass. */
  #onClose = () => {
    this.#stop();
    this.#ran = false;
  };

  #start() {
    this.#place();
    this.hidden = false;
    this.#input.classList.add('lasura-search-inspiring');

    if (reducedMotion()) {
      this.classList.add('is-still');
      this.#typed.textContent = this.#ideas[Math.floor(Math.random() * this.#ideas.length)];
      return;
    }

    this.#typed.textContent = '';
    this.#later(() => this.#step(0, 0, false), START_MS);
  }

  #stop = () => {
    window.clearTimeout(this.#timer);
    this.hidden = true;
    this.classList.remove('is-still');
    this.#input?.classList.remove('lasura-search-inspiring');
  };

  #later(fn, ms) {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(fn, ms);
  }

  #step(index, length, deleting) {
    const idea = this.#ideas[index];

    if (!deleting) {
      const next = length + 1;
      this.#typed.textContent = idea.slice(0, next);
      if (next < idea.length) return this.#later(() => this.#step(index, next, false), TYPE_MS);
      if (index === this.#ideas.length - 1) return this.#later(this.#stop, HOLD_MS * 1.5);
      return this.#later(() => this.#step(index, next, true), HOLD_MS);
    }

    const next = length - 1;
    this.#typed.textContent = idea.slice(0, next);
    if (next > 0) return this.#later(() => this.#step(index, next, true), DELETE_MS);
    this.#later(() => this.#step(index + 1, 0, false), GAP_MS);
  }

  /* Sit exactly over the field's text, whatever padding and font Horizon gives it. */
  #place() {
    const host = this.parentElement.getBoundingClientRect();
    const field = this.#input.getBoundingClientRect();
    const style = window.getComputedStyle(this.#input);
    const padStart = parseFloat(style.paddingLeft) || 0;
    const padEnd = parseFloat(style.paddingRight) || 0;

    this.style.left = `${field.left - host.left + padStart}px`;
    this.style.top = `${field.top - host.top}px`;
    this.style.height = `${field.height}px`;
    this.style.bottom = 'auto';
    this.style.width = `${Math.max(0, field.width - padStart - padEnd)}px`;
    this.style.font = style.font;
    this.style.letterSpacing = style.letterSpacing;
  }
}

class LasuraSearchIdeas extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-idea]') : null;
    const input = this.closest('predictive-search-component')?.querySelector('input.search-input');
    if (!link || !input) return;

    event.preventDefault();
    input.value = link.dataset.idea ?? link.textContent.trim();
    input.focus();
    /* Horizon's search handler ignores input events without an inputType. */
    input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: input.value }));
  };
}

if (!customElements.get('lasura-search-inspiration')) {
  customElements.define('lasura-search-inspiration', LasuraSearchInspiration);
}
if (!customElements.get('lasura-search-ideas')) {
  customElements.define('lasura-search-ideas', LasuraSearchIdeas);
}
